import { Button } from "@/components/ui/button";
import { useHashNavigation } from "@/hooks/useHashNavigation";
import { cn } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Hammer, Scale, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_public/terms")({
  component: TermsComponent,
});

function TermsComponent() {
  const { t } = useTranslation();
  const { navTo, activeHash } = useHashNavigation("/terms");

  const sections = ["introduction", "accounts", "gold-handling", "termination", "liability"];

  return (
    <div className="min-h-screen bg-background py-12 md:py-20">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Back Link Wrapper */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navTo()}
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            <span>{t("legal.shared.backToHome")}</span>
          </Button>
        </div>

        {/* Header Block */}
        <div className="border-b pb-8 mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-wider mb-2">
              <Scale className="h-4 w-4" />
              <span>{t("legal.terms.label")}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              {t("legal.terms.title")}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {t("legal.shared.lastUpdated")}: {new Date().toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-medium text-muted-foreground">
            <Hammer className="h-3.5 w-3.5 text-primary" />
            <span>{t("landing.header.brand")} Platform</span>
          </div>
        </div>

        {/* Dynamic Two-Column Document Layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Quick-Jump Sticky Navigation Sidebar */}
          <aside className="hidden md:block md:col-span-1 sticky top-24 h-fit">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
              {t("legal.shared.navigation")}
            </p>
            <nav className="flex flex-col gap-2 border-s border-border">
              {sections.map((sec) => (
                <button
                  key={sec}
                  onClick={() => navTo(sec)}
                  className={cn(
                    "text-xs text-start border-s-2 border-transparent -ms-px ps-4 py-1 transition-colors block w-full bg-transparent cursor-pointer",
                    activeHash === sec
                      ? "text-primary border-primary font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t(`legal.terms.sections.${sec}.nav`)}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Legal Copy Text Area */}
          <main className="col-span-1 md:col-span-3 space-y-10 leading-relaxed text-sm text-muted-foreground">
            {sections.map((sec) => (
              <section key={sec} id={sec} className="scroll-mt-24 space-y-3">
                <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                  <span className="text-xs font-mono text-primary/50">//</span>
                  {t(`legal.terms.sections.${sec}.heading`)}
                </h2>
                <p className="whitespace-pre-line text-sm leading-relaxed">
                  {t(`legal.terms.sections.${sec}.content`)}
                </p>
              </section>
            ))}

            {/* Crucial Advisory Callout Box */}
            <div className="p-4 rounded-xl border border-destructive/20 bg-destructive/5 flex gap-3 text-xs text-destructive">
              <ShieldAlert className="h-5 w-5 shrink-0 text-destructive" />
              <p className="leading-normal font-medium">{t("legal.terms.advisoryDisclaimer")}</p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
